import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

// search module
import { NgPipesModule } from 'ngx-pipes';
import { Ng2ImgMaxModule } from 'ng2-img-max';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { LayoutsModule } from "./layouts/layouts.module";
import { PagesModule } from "./pages/pages.module";

// Auth
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { initFirebaseBackend } from './authUtils';
import { FakeBackendInterceptor } from './core/helpers/fake-backend';
import { ErrorInterceptor } from './core/helpers/error.interceptor';
import { JwtInterceptor } from './core/helpers/jwt.interceptor';

// Language
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { StoreModule } from '@ngrx/store';
import { rootReducer } from './store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { AuthenticationEffects } from './store/Authentication/authentication.effects';
import { ServicesModule } from './services/services.module';
import { APP_BASE_HREF } from '@angular/common';
import { AuthInterceptor } from './core/helpers/auth.interceptor';
import { MenuService } from './services/menu/menu.services';
import { firstValueFrom } from 'rxjs';
import { StorageKeyEnum } from './services/storage/storageKeyEnum.enum';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { AuthenticationService } from './services/authentication/authentication.service';
import { LocalizationService } from './services/localization/localization.service';
import { provideNgxMask } from 'ngx-mask';

 

function hasRawToken(): boolean {
  // ⚠️ Cambiá la clave según tu app (o usá tu StorageService si es sync).
  const raw = localStorage.getItem('carrier-' + StorageKeyEnum.sessionToken);
  return typeof raw === 'string' && raw.trim().length > 0 && raw !== 'undefined' && raw !== 'null';
}

export function initTokenAndI10n(
  auth: AuthenticationService,
  loc: LocalizationService,
  translate: TranslateService
) {
  return async () => {
    try {
      // 1) actualiza token y hace lo que ya tengas adentro
      await auth.updateFirebaseToken();

      // 2) (opcional) si updateFirebaseToken NO llama a translate.use(),
      // aplicá el idioma acá:
      const cc = (auth as any)._identityService?.countryCode || 'US';
      const locale = await firstValueFrom(loc.getCountryLocaleSettings(cc));
      // si tu servicio devuelve algo como { lang: 'es-AR' }:
      if ((locale as any)?.lang) {
        translate.setDefaultLang((locale as any).lang);
        translate.use((locale as any).lang);
      }
    } catch (e) {
      // no bloquees el bootstrap por errores de red
      console.error('initTokenAndI18n error', e);
    }
  };
}

export function preloadMenuIfAuth(menu: MenuService) {
  return () => {
    if (!hasRawToken()) {
      // No token → no hago request y NO rompo el bootstrap.
      return Promise.resolve();
    }
    // Hay token → precargo menú
    return firstValueFrom(menu.getCarrierMenu());
  };
}

// if (environment.firebase) {
//   initFirebaseBackend(environment.firebase);
// } else {
//   FakeBackendInterceptor;
// }

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    TranslateModule.forRoot({
      fallbackLang: 'en-us'
    }),
    BrowserAnimationsModule,
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    LayoutsModule,
    PagesModule,
    NgPipesModule,
    Ng2ImgMaxModule,
    NgbModule,
    StoreModule.forRoot(rootReducer),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    EffectsModule.forRoot([
      AuthenticationEffects,
    ]),
    ServicesModule
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/', },
    // { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: FakeBackendInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    // 1) tu initializer existente (menú)
    { provide: APP_INITIALIZER, useFactory: preloadMenuIfAuth, deps: [MenuService], multi: true },

    // 2) NUEVO initializer: token + i10n
    {
      provide: APP_INITIALIZER,
      useFactory: initTokenAndI10n,
      deps: [AuthenticationService, LocalizationService, TranslateService],
      multi: true
    },
    ...provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json' }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideDatabase(() => getDatabase()),
    provideNgxMask(),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
